import { useEffect, useState } from 'react';
import { notify } from '@/lib/notify';
import { Link } from 'react-router-dom';
import {
  calendarFeedUrl,
  downloadCurrentMonthIcs,
  getMyStaffRecord,
  rotateCalendarToken,
} from '@/lib/notifications';
import type { Staff } from '@/lib/types';

/**
 * /my-activity/notifications -- per-staff reminder preferences.
 *
 * Scope of v1 (slice 0097):
 *   * Schedule publication emails: always-on transactional notice
 *   * Calendar sync: personal subscription URL + on-demand .ics download
 *   * Weekly digest + day-before reminder: visible as "coming soon" rows
 *     so the user sees what's on the way (we'll wire toggles in a
 *     follow-up slice).
 *
 * The page is reached from the email's "Manage my reminders" link, from
 * the "Add to my calendar" CTA in the same email, and from the /my-activity
 * landing page.
 */
export default function NotificationPreferences() {
  const [staff, setStaff] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const me = await getMyStaffRecord();
        if (!cancelled) setStaff(me);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onCopy() {
    if (!staff?.calendar_feed_token) return;
    const url = calendarFeedUrl(staff.calendar_feed_token);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setError('Could not copy to clipboard. Select the URL manually.');
    }
  }

  async function onRotate() {
    if (!staff) return;
    const ok = window.confirm(
      'Generate a new calendar URL? Your current URL will stop working immediately — you will need to re-subscribe in your calendar app.'
    );
    if (!ok) return;
    setRotating(true);
    setError(null);
    try {
      const updated = await rotateCalendarToken(staff.id);
      notify.success('Calendar URL rotated');
      setStaff(updated);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRotating(false);
    }
  }

  function onDownload() {
    if (!staff?.calendar_feed_token) return;
    downloadCurrentMonthIcs(staff.calendar_feed_token, 'pharmasync-shifts.ics');
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            <Link to="/my-activity" className="hover:underline">
              ← My activity
            </Link>
          </p>
          <h1 className="text-2xl font-semibold tracking-tight">
            Notification preferences
          </h1>
          <p className="text-sm text-muted-foreground">
            Choose how PharmaSync reminds you about your shifts.
          </p>
        </div>

        {loading && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}

        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        {!loading && !staff && (
          <div className="rounded-md border border-dashed border-border bg-muted/40 p-6 text-sm text-muted-foreground">
            You don&rsquo;t have a staff record linked to your account, so
            there&rsquo;s nothing to configure here yet. Ask your manager to
            link you to a staff record.
          </div>
        )}

        {staff && (
          <>
            {/* SECTION 1: Schedule publications (always on) */}
            <section className="rounded-lg border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-medium">Schedule publication emails</h2>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Get an email when your manager publishes a new monthly schedule.
                  </p>
                </div>
                <span className="inline-flex shrink-0 items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                  Always on
                </span>
              </div>
            </section>

            {/* SECTION 2: Calendar sync */}
            <section className="rounded-lg border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-medium">Sync to your calendar</h2>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Add your shifts to Google Calendar, Apple Calendar, or Outlook.
                    Subscribe to the URL below in your calendar app and it stays up to date
                    automatically when your manager publishes or edits a schedule.
                  </p>
                </div>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Your personal calendar feed (keep this URL private):
              </p>
              <div className="mt-2 break-all rounded-md bg-muted/60 px-3 py-2 font-mono text-xs text-foreground">
                {staff.calendar_feed_token
                  ? calendarFeedUrl(staff.calendar_feed_token)
                  : '— no token —'}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onCopy}
                  disabled={!staff.calendar_feed_token}
                  className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  {copied ? 'Copied!' : 'Copy URL'}
                </button>
                <button
                  type="button"
                  onClick={onDownload}
                  disabled={!staff.calendar_feed_token}
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
                >
                  Download .ics
                </button>
                <button
                  type="button"
                  onClick={onRotate}
                  disabled={rotating}
                  className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
                  title="Invalidate the current URL and mint a fresh one"
                >
                  {rotating ? 'Generating…' : 'Generate new URL'}
                </button>
              </div>
              <details className="mt-3 text-xs text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground">
                  How to subscribe in your calendar app
                </summary>
                <ul className="mt-2 space-y-1.5 list-disc pl-5">
                  <li>
                    <strong>Google Calendar:</strong> Other calendars → +
                    → From URL → paste the URL above
                  </li>
                  <li>
                    <strong>Apple Calendar (Mac):</strong> File → New Calendar
                    Subscription → paste the URL
                  </li>
                  <li>
                    <strong>Apple Calendar (iPhone):</strong> Settings → Calendar
                    → Accounts → Add Account → Other → Add Subscribed Calendar
                  </li>
                  <li>
                    <strong>Outlook:</strong> Add calendar → Subscribe from web →
                    paste the URL
                  </li>
                </ul>
              </details>
            </section>

            {/* SECTION 3: Weekly digest (coming soon) */}
            <section className="rounded-lg border border-border bg-card p-5 opacity-70">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-medium">Weekly digest</h2>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    A summary of the week ahead, every Sunday evening.
                  </p>
                </div>
                <span className="shrink-0 text-xs italic text-muted-foreground">
                  Coming soon
                </span>
              </div>
            </section>

            {/* SECTION 4: Day-before reminder (coming soon) */}
            <section className="rounded-lg border border-border bg-card p-5 opacity-70">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-medium">Day-before reminder</h2>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    A reminder the evening before each shift.
                  </p>
                </div>
                <span className="shrink-0 text-xs italic text-muted-foreground">
                  Coming soon
                </span>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
